import {NgModel} from '@angular/forms';
import {IDeviceData} from '../interfaces/juconnGateway.response';
import {ShareService} from '../services/share.service';
import {IInstallationDataResponse} from '../interfaces/installation-data.response';

export class Utils {
  public static async getImageAsBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result as string;
        // Check if it's a valid base64 string
        if (/^data:image\/([a-zA-Z]*);base64,/.test(base64String)) {
          resolve(base64String);
        } else {
          reject('Invalid image format');
        }
      };

      reader.onerror = () => {
        reject('Error reading the file');
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   *
   * @param file
   * @param maxSize
   * @param asDataUrl - if ture returns dataUrl otherwise it returns blob
   * @param imageQuality
   */
  public static async resizeImage(
    file: File,
    maxSize: number,
    asDataUrl = false,
    imageQuality = 90,
  ): Promise<string | Blob> {
    if (imageQuality !== 90) {
      console.log('resizeImage-output quality set to:', imageQuality);
    }

    const reader = new FileReader();
    const image = new Image();
    const canvas = document.createElement('canvas');
    const dataURItoBlob = (dataURI: string) => {
      const bytes =
        dataURI.split(',')[0].indexOf('base64') >= 0
          ? atob(dataURI.split(',')[1])
          : window.unescape(dataURI.split(',')[1]);
      const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
      const max = bytes.length;
      const ia = new Uint8Array(max);
      for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
      return new Blob([ia], {type: mime});
    };
    const resize = () => {
      let width = image.width;
      let height = image.height;

      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      canvas?.getContext('2d')?.drawImage(image, 0, 0, width, height);
      let dataUrl = canvas.toDataURL('image/jpeg', imageQuality / 100);
      return asDataUrl ? dataUrl : dataURItoBlob(dataUrl);
    };

    return new Promise((ok, no) => {
      if (!(file && file.type.match(/image.*/))) {
        no(new Error('Not an image'));
        return;
      }

      reader.onload = (readerEvent: any) => {
        image.onload = () => ok(resize());
        image.src = readerEvent.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  public static markControlAsTouched(ngModel: NgModel) {
    setTimeout((_: any) => {
      ngModel.control.markAllAsTouched();
    }, 500);
  }

  public static replaceObjectContent(obj: any, objWithNewContent: Object = {}) {
    // Remove all properties from the old object
    for (let key in obj) {
      delete obj[key];
    }

    // Add properties from the new object to the old object
    for (let key in objWithNewContent) {
      obj[key] = objWithNewContent[key];
    }
  }

  public static deepCopy(obj: any): any {
    let copy: any;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' != typeof obj) return obj;

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (let attr in obj) {
        if (obj.hasOwnProperty(attr)) copy[attr] = this.deepCopy(obj[attr]);
      }
      return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  }

  public static initPumpSensorIds(deviceData: IDeviceData): any[] {
    const sensorIdOptions = [];
    if (deviceData?.uvsModule?.length > 0) {
      for (const module of deviceData.uvsModule) {
        for (let i = 1; i <= module.currentLoop?.length; i++) {
          sensorIdOptions.push({value: module.moduleInfo.address + '.' + i});
        }
      }
    }
    return sensorIdOptions;
  }

  public static setDataModelsFromInstalledData(
    shareService: ShareService,
    installationData: IInstallationDataResponse,
  ) {
    if (!installationData?.productSpecificData) {
      return;
    }

    const productData = installationData.productSpecificData;

    const generalGateway = shareService.generalGateway;
    if (productData?.uvsModules?.length > 0) {
      const uvsModules = [];
      for (const uvsModule of productData.uvsModules) {
        uvsModules.push({
          modulId: uvsModule.modulId,
          uvsType: uvsModule.uvsType,
          address: uvsModule.address,
        });
      }
      if (uvsModules.length) {
        generalGateway.uvsModules = uvsModules;
      }
    }
    const customer = shareService.customerOptions.find(
      (c) => c.customerId === productData.customerId,
    );
    generalGateway.selectedCustomer =
      customer || generalGateway.selectedCustomer;
    generalGateway.selectedSite =
      [...customer.plannedSites, ...customer.installedSites].find(
        (s) => s.id === productData.siteId,
      ) || generalGateway.selectedSite;
    generalGateway.addressComment = productData.addressComment;
    generalGateway.locationComment = productData.locationComment;

    shareService.generalGateway = generalGateway;

    const generalMeasurement = shareService.generalMeasurement;
    const installationMaterial = productData.materials.find(
      (m) => m.materialType === 'INSTALLATION',
    );
    generalMeasurement.installationMaterial =
      installationMaterial || generalMeasurement.installationMaterial;

    shareService.generalMeasurement = generalMeasurement;

    const generalPrePhotos = shareService.generalPrePhotos;
    generalPrePhotos.preInstallHeatingImages =
      productData.preInstallHeatingImages.map((image) => ({
        image: image.image,
      })) || generalPrePhotos.preInstallHeatingImages;
    generalPrePhotos.preInstallBoilerRoomImages =
      productData.preInstallBoilerImages.map((image) => ({
        image: image.image,
      })) || generalPrePhotos.preInstallBoilerRoomImages;
    shareService.generalPrePhotos = generalPrePhotos;

    const generalSending = shareService.generalSending;
    generalSending.installationComment = productData.installationComment;
    generalSending.generalComment = productData.comment;
    generalSending.nonInstallableSensors = productData.nonInstallableSensors;
    shareService.generalSending = generalSending;
  }
}
